import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Search, Download, FileText, Users, Calendar, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';

interface PolicyAcknowledgment {
  id: string;
  user_id: string;
  user_type: string;
  privacy_policy_accepted: boolean;
  tos_accepted: boolean;
  cookies_accepted: boolean;
  data_rights_acknowledged: boolean;
  parental_consent: boolean | null;
  parent_name: string | null;
  parent_email: string | null;
  privacy_policy_version: string;
  tos_version: string;
  acknowledged_at: string;
  needs_reacknowledgment: boolean;
  username?: string;
  email?: string;
}

export function PolicyManagementAdmin() {
  const [acknowledgments, setAcknowledgments] = useState<PolicyAcknowledgment[]>([]);
  const [filteredAcknowledgments, setFilteredAcknowledgments] = useState<PolicyAcknowledgment[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterUserType, setFilterUserType] = useState('all');

  useEffect(() => {
    loadAcknowledgments();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [searchQuery, filterStatus, filterUserType, acknowledgments]);

  const loadAcknowledgments = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_policy_acknowledgments')
        .select('*')
        .order('acknowledged_at', { ascending: false });

      if (error) throw error;

      // Fetch user details for each acknowledgment
      const enrichedData = await Promise.all(
        (data || []).map(async (ack) => {
          let username = 'Unknown';
          let email = 'Unknown';

          if (ack.user_type === 'supabase') {
            const { data: profile } = await supabase
              .from('profiles')
              .select('username, email')
              .eq('id', ack.user_id)
              .single();
            if (profile) {
              username = profile.username || 'Unknown';
              email = profile.email || 'Unknown';
            }
          } else {
            const { data: internalUser } = await supabase
              .from('internal_users')
              .select('username')
              .eq('id', ack.user_id)
              .single();
            if (internalUser) {
              username = internalUser.username || 'Unknown';
              email = 'Internal User';
            }
          }

          return { ...ack, username, email };
        })
      );

      setAcknowledgments(enrichedData);
    } catch (error) {
      console.error('Error loading acknowledgments:', error);
      toast.error('Failed to load policy acknowledgments');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...acknowledgments];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (ack) =>
          ack.username?.toLowerCase().includes(query) ||
          ack.email?.toLowerCase().includes(query) ||
          ack.user_id.toLowerCase().includes(query)
      );
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter((ack) => {
        if (filterStatus === 'complete') {
          return (
            ack.privacy_policy_accepted &&
            ack.tos_accepted &&
            ack.cookies_accepted &&
            ack.data_rights_acknowledged &&
            !ack.needs_reacknowledgment
          );
        } else if (filterStatus === 'incomplete') {
          return (
            !ack.privacy_policy_accepted ||
            !ack.tos_accepted ||
            !ack.cookies_accepted ||
            !ack.data_rights_acknowledged
          );
        } else if (filterStatus === 'needs_reack') {
          return ack.needs_reacknowledgment;
        }
        return true;
      });
    }

    // User type filter
    if (filterUserType !== 'all') {
      filtered = filtered.filter((ack) => ack.user_type === filterUserType);
    }

    setFilteredAcknowledgments(filtered);
  };

  const exportToCSV = () => {
    const headers = [
      'User ID',
      'Username',
      'Email',
      'User Type',
      'Privacy Policy',
      'ToS',
      'Cookies',
      'Data Rights',
      'Parental Consent',
      'PP Version',
      'ToS Version',
      'Acknowledged At',
      'Needs Reacknowledgment',
    ];

    const rows = filteredAcknowledgments.map((ack) => [
      ack.user_id,
      ack.username,
      ack.email,
      ack.user_type,
      ack.privacy_policy_accepted ? 'Yes' : 'No',
      ack.tos_accepted ? 'Yes' : 'No',
      ack.cookies_accepted ? 'Yes' : 'No',
      ack.data_rights_acknowledged ? 'Yes' : 'No',
      ack.parental_consent === null ? 'N/A' : ack.parental_consent ? 'Yes' : 'No',
      ack.privacy_policy_version,
      ack.tos_version,
      new Date(ack.acknowledged_at).toISOString(),
      ack.needs_reacknowledgment ? 'Yes' : 'No',
    ]);

    const csvContent = [headers, ...rows].map((row) => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `policy-acknowledgments-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Compliance report exported');
  };

  const getComplianceStatus = (ack: PolicyAcknowledgment) => {
    if (ack.needs_reacknowledgment) {
      return { label: 'Reacknowledgment Required', variant: 'destructive' as const, icon: AlertCircle };
    }
    if (
      ack.privacy_policy_accepted &&
      ack.tos_accepted &&
      ack.cookies_accepted &&
      ack.data_rights_acknowledged
    ) {
      return { label: 'Complete', variant: 'default' as const, icon: CheckCircle2 };
    }
    return { label: 'Incomplete', variant: 'secondary' as const, icon: XCircle };
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Policy Acknowledgment Management
            </CardTitle>
            <Button onClick={exportToCSV} variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Export Report
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filters */}
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label>Search Users</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Username, email, or ID"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Compliance Status</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="complete">Complete</SelectItem>
                  <SelectItem value="incomplete">Incomplete</SelectItem>
                  <SelectItem value="needs_reack">Needs Reacknowledgment</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>User Type</Label>
              <Select value={filterUserType} onValueChange={setFilterUserType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="supabase">Supabase Users</SelectItem>
                  <SelectItem value="internal">Internal Users</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Stats Summary */}
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <div className="text-sm text-muted-foreground">Total</div>
                </div>
                <p className="text-2xl font-bold">{filteredAcknowledgments.length}</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  <div className="text-sm text-muted-foreground">Complete</div>
                </div>
                <p className="text-2xl font-bold">
                  {
                    filteredAcknowledgments.filter(
                      (a) =>
                        a.privacy_policy_accepted &&
                        a.tos_accepted &&
                        a.cookies_accepted &&
                        a.data_rights_acknowledged &&
                        !a.needs_reacknowledgment
                    ).length
                  }
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-yellow-500" />
                  <div className="text-sm text-muted-foreground">Needs Reack.</div>
                </div>
                <p className="text-2xl font-bold">
                  {filteredAcknowledgments.filter((a) => a.needs_reacknowledgment).length}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-blue-500" />
                  <div className="text-sm text-muted-foreground">Parental Consent</div>
                </div>
                <p className="text-2xl font-bold">
                  {filteredAcknowledgments.filter((a) => a.parental_consent === true).length}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Table */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Policies</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      Loading acknowledgments...
                    </TableCell>
                  </TableRow>
                ) : filteredAcknowledgments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No acknowledgments found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAcknowledgments.map((ack) => {
                    const status = getComplianceStatus(ack);
                    const StatusIcon = status.icon;
                    return (
                      <TableRow key={ack.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{ack.username}</div>
                            <div className="text-xs text-muted-foreground">{ack.email}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{ack.user_type}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={status.variant} className="flex items-center gap-1 w-fit">
                            <StatusIcon className="h-3 w-3" />
                            {status.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {ack.privacy_policy_accepted ? (
                              <CheckCircle2 className="h-4 w-4 text-green-500" />
                            ) : (
                              <XCircle className="h-4 w-4 text-red-500" />
                            )}
                            {ack.tos_accepted ? (
                              <CheckCircle2 className="h-4 w-4 text-green-500" />
                            ) : (
                              <XCircle className="h-4 w-4 text-red-500" />
                            )}
                            {ack.cookies_accepted ? (
                              <CheckCircle2 className="h-4 w-4 text-green-500" />
                            ) : (
                              <XCircle className="h-4 w-4 text-red-500" />
                            )}
                            {ack.data_rights_acknowledged ? (
                              <CheckCircle2 className="h-4 w-4 text-green-500" />
                            ) : (
                              <XCircle className="h-4 w-4 text-red-500" />
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-xs">
                            <div>PP: {ack.privacy_policy_version}</div>
                            <div>ToS: {ack.tos_version}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-xs">
                            {new Date(ack.acknowledged_at).toLocaleDateString()}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
